// Orion Fund portfolio — shown in the dashboard's "Orion Fund Deployment" section.
// `program: 'genesis-1'` marks companies that went through the Genesis Pre Accelerator (Cohort 1),
// `program: 'apex-1'` the Apex Growth Accelerator (Cohort 1). Companies in more than one cohort
// keep their first cohort in `program` and list the others in `alsoIn`.
// Genesis company names, sectors, one-liners are from the Genesis Cohort Report
// (09/25/2026). Investment amounts, fundraising, jurisdiction and internal assessments are
// intentionally NOT published here.

export const PORTFOLIO = [
  {
    name: 'Spout Finance',
    logoBg: 'light', // transparent logo — shown on a light tile
    sector: 'RWA',
    tagline: 'Tokenized real-world assets — borrow against your stocks on-chain.',
    url: 'https://spout.finance/',
    logo: '/portfolio/spout.png',
  },
  {
    name: 'Skalor',
    sector: 'AI',
    tagline: 'The fiduciary layer for agentic payments.',
    url: 'https://www.skalor.xyz/',
    logo: '/portfolio/skalor.png',
  },
  {
    name: 'Mogul',
    logo: '/portfolio/mogul.png',
    url: 'https://www.mogul.club/',
    sector: 'RWA',
    tagline: 'Fractional real estate investing, made simple.',
    about: 'A fractional real estate investing platform founded by Goldman Sachs alumni, letting investors own a share of real estate with monthly payouts.',
  },
  {
    name: 'LX Research',
    logo: '/portfolio/lx-research.png',
    logoBg: 'dark', // transparent logo — shown on a dark tile
    url: 'https://lx.dev/bitcoin-clock/',
    sector: 'BTC-Fi',
    tagline: 'Privacy-preserving swaps and dark pools for bitcoin exchanges.',
    about: 'LX builds bitcoin dark-pool infrastructure — the easiest way for exchanges to add privacy-preserving swaps.',
  },
  {
    name: 'Helios',
    logo: '/portfolio/helios.png',
    url: 'https://www.heliosfinance.xyz/',
    sector: 'BTC-Fi',
    tagline: 'Trustless Bitcoin lending — borrow dollars against your BTC in one block.',
    about: 'A trustless lending protocol enabling instant, Bitcoin-backed dollar loans on a decentralized platform.',
  },
  {
    name: 'Arc',
    logo: '/portfolio/arc.png',
    url: 'https://www.arc.network/',
    sector: 'Infrastructure',
    tagline: "Circle's open Layer-1 blockchain purpose-built for stablecoin finance.",
    about: 'Arc is the open Layer-1 blockchain from Circle, the issuer of USDC, built as an economic operating system for stablecoin finance on the internet.',
  },
  {
    name: 'Maestro',
    logo: '/portfolio/maestro.png',
    url: 'https://gomaestro.org/',
    sector: 'BTC-Fi',
    tagline: 'Institutional Bitcoin yield, anchored by miner credit and productive markets.',
    about: 'Maestro delivers Bitcoin yield instruments through structured vaults with delta-neutral hedging, backed by miner credit and productive Bitcoin markets.',
  },
  {
    name: 'ClipStake',
    logo: '/portfolio/clipstake.png',
    url: 'https://www.clipstake.com/',
    sector: 'Consumer',
    tagline: 'Pay-per-view marketplace connecting brands with content creators.',
    about: 'ClipStake connects brands with content creators through pay-per-view campaigns, so brands pay for real views.',
  },
  {
    name: 'ADApose',
    sector: 'RWA',
    tagline: 'Verifiable attestation infrastructure for non-price, real-world data.',
    about: 'An attestation layer turning public records into on-chain facts.',
    program: 'genesis-1',
  },
  {
    name: 'Agentic Dynamics',
    logo: '/portfolio/agentic-dynamics.svg',
    url: 'https://cardano402.com/',
    sector: 'AI',
    tagline: 'Authorized procurement layer for AI agents accessing gated content and services.',
    about: 'A procurement and entitlement layer for AI agents: finding an authorized source, checking entitlements and delivering content with rights, provenance and a receipt. Formerly Agent-to-agent.xyz.',
    program: 'genesis-1',
  },
  {
    name: 'BETTER',
    logo: '/portfolio/better.png',
    sector: 'Consumer',
    tagline: 'Onboarding, discovery, and distribution hub for the Cardano ecosystem.',
    about: 'A discovery and onboarding hub connecting users and Cardano apps.',
    program: 'genesis-1',
  },
  {
    name: 'ChatterPay',
    logo: '/portfolio/x-chatterpay.jpg', // X (Twitter) profile picture
    avatar: true,
    url: 'https://chatterpay.net/',
    sector: 'Payments',
    tagline: 'White-label WhatsApp payments infrastructure for fintechs, now live on Cardano.',
    about: 'A WhatsApp-native crypto payments SDK for fintechs.',
    program: 'genesis-1',
    alsoIn: ['apex-1'],
  },
  {
    name: 'ENVNT',
    logo: '/portfolio/envnt.png',
    logoBg: 'light', // transparent logo — shown on a light tile
    url: 'https://envnt.io/',
    sector: 'Consumer',
    tagline: 'All-in-one operating system for mid-market conferences and trade shows.',
    about: 'A unified event OS for ticketing, registration, CRM, sponsors, floor plans, communications and analytics — replacing the 5–8 tools organizers typically juggle.',
    program: 'genesis-1',
  },
  {
    name: 'GravityDEX',
    logo: '/portfolio/gravitydex.png',
    url: 'https://gravitydex.app/',
    sector: 'DeFi',
    tagline: 'Censorship-resistant Cardano DEX with no operator-controlled server dependency.',
    about: 'A Cardano DEX that runs without operator-controlled servers.',
    program: 'genesis-1',
  },
  {
    name: 'Lava',
    logo: '/portfolio/lava.png',
    url: 'https://lava.markets/',
    sector: 'DeFi',
    tagline: 'Liquid staking standard for Cardano — stake ADA, mint L-ADA, and deploy it across DeFi.',
    about: 'A liquid staking token (L-ADA) for Cardano DeFi.',
    program: 'genesis-1',
  },
  {
    name: 'Nexos Workspace',
    logo: '/portfolio/nexos.png',
    logoBg: 'dark', // transparent logo — shown on a dark tile
    url: 'https://www.nexosworkspace.com/home',
    sector: 'AI',
    tagline: 'Unified workspace and AI orchestration layer for small Web3 teams.',
    about: 'An AI-powered unified workspace for Web3 teams.',
    program: 'genesis-1',
  },
  {
    name: 'Ospree',
    logo: '/portfolio/x-ospree.jpg', // X (Twitter) profile picture
    avatar: true,
    sector: 'Infrastructure',
    tagline: 'Powering compliance for a global digital economy.',
    about: 'A modular compliance platform that helps crypto-asset companies and financial institutions automate Travel Rule and AML/CTF requirements across multiple blockchains.',
    url: 'https://www.ospree.io/',
    program: 'genesis-1',
    alsoIn: ['apex-1'],
  },
  {
    name: 'riverArk',
    logo: '/portfolio/riverark.png',
    url: 'https://riverark.io/',
    sector: 'Payments',
    tagline: 'Bitcoin Lightning payments with dollar settlement for unattended retail.',
    about: 'Lightning-to-dollar payments for vending machines and kiosks.',
    program: 'genesis-1',
  },
  {
    name: 'Tata imali Rewards',
    logo: '/portfolio/tataimali.png',
    logoBg: 'light', // transparent logo — shown on a light tile
    url: 'https://www.tataimalirewards.com/',
    sector: 'Payments',
    tagline: 'Stablecoin-powered payments, cashback, and loyalty for South African retailers.',
    about: 'Stablecoin cashback and loyalty for South African retailers.',
    program: 'genesis-1',
  },
  {
    name: 'Moshi Concepts',
    logo: '/portfolio/moshi.png',
    url: 'https://bounties.learncardano.io/',
    sector: 'Payments',
    tagline: 'Creating scalable solutions on Cardano from escrow services to trusted stablecoin payments.',
    about: 'Creating scalable solutions on Cardano from escrow services to trusted stablecoin payments. Products include Web3 Bounties, an on-chain escrow engine guaranteeing contributors get paid for freelance and bounty work.',
    program: 'genesis-1',
  },
  // ---- Apex Growth Accelerator, Cohort 1 (2026) ----
  {
    name: 'Kenomic',
    logo: '/portfolio/x-kenomic.jpg', // X (Twitter) profile picture
    avatar: true,
    url: 'https://kenomic.ai/',
    sector: 'AI',
    tagline: 'AI-powered token launch infrastructure, from design to live execution.',
    about: 'End-to-end, AI-powered token launch infrastructure to design, validate, launch and manage a token.',
    program: 'apex-1',
  },
  {
    name: 'Ascent Rivals',
    logo: '/portfolio/x-ascent-rivals.jpg', // X (Twitter) profile picture
    avatar: true,
    url: 'https://www.ascentrivals.com/',
    sector: 'Gaming',
    tagline: 'High-speed sci-fi combat racing.',
    about: 'A competitive combat racing game: race customizable ships through high-speed circuits and survive the final Ascension.',
    program: 'apex-1',
  },
  {
    name: 'Origami Tech',
    logo: '/portfolio/x-origami.jpg', // X (Twitter) profile picture
    avatar: true,
    url: 'https://www.origami.tech/',
    sector: 'DeFi',
    tagline: 'Automated crypto trading bots for any exchange.',
    about: 'Origami Tech builds automated trading bots that run strategies across centralized and decentralized exchanges.',
    program: 'apex-1',
  },
  {
    name: 'Mentioned',
    logo: '/portfolio/x-mentioned.jpg', // X (Twitter) profile picture
    avatar: true,
    url: 'https://www.mentioned.market/',
    sector: 'Consumer',
    tagline: 'Mention markets: trade on what gets said.',
    about: 'A prediction market platform for trading on which words get mentioned in speeches, podcasts and events.',
    program: 'apex-1',
  },
  {
    name: 'Nomadz',
    logo: '/portfolio/x-nomadz.jpg', // X (Twitter) profile picture
    avatar: true,
    url: 'https://nomadz.xyz/',
    sector: 'Consumer',
    tagline: 'Flights and 1M+ stays at lower rates, paid by card or crypto.',
    about: 'A travel booking platform for flights and stays, payable by card or crypto.',
    program: 'apex-1',
  },
  {
    name: 'Masumi',
    logo: '/portfolio/x-masumi.jpg', // X (Twitter) profile picture
    avatar: true,
    url: 'https://www.masumi.network/',
    sector: 'AI',
    tagline: 'The payment network for AI agents.',
    about: 'Escrow smart contracts, on-chain identity and a public registry that let autonomous AI agents transact without trusting each other.',
    program: 'apex-1',
  },
];

export const PROGRAM_TAGS = {
  'genesis-1': 'Genesis Cohort 1',
  'apex-1': 'Apex Cohort 1',
};

/** Companies that went through a given accelerator cohort (e.g. 'apex-1'). */
export const inCohort = (key) => PORTFOLIO.filter((c) => c.program === key || c.alsoIn?.includes(key));
